/**
 * Resources Calendar Header Component
 *
 * Navigation header for the resources calendar view.
 * Includes day navigation, today button, menu, and add event.
 *
 * Follows single responsibility principle: only renders header UI.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors, Shadows } from '@/constants/theme';
import { BorderRadius } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import { ResourcesCalendarManager } from '@/lib/calendar/ResourcesCalendarManager';
import { ScreenHeaderContainer } from '@/components/ui/ScreenHeaderContainer';
import { AddEventDropdown } from './AddEventDropdown';
import type { ResourcesCalendarHeaderProps } from '@/types/resources-calendar';

export function ResourcesCalendarHeader({
  selectedDate,
  onPrevDay,
  onNextDay,
  onTodayPress,
  onMenuPress,
  onAddEventPress,
  onHeaderTextPress,
  accentColor,
  showCalendarSelector,
  bareContainer = false,
}: ResourcesCalendarHeaderProps & { bareContainer?: boolean }) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const headerText = ResourcesCalendarManager.formatHeaderDate(selectedDate);

  // When bareContainer is true, the parent supplies the rounded/shadow/safe-area
  // wrapper that visually extends past this header (e.g. to include avatars +
  // "All day" row), so we just render the bare row of controls.
  const Wrapper = bareContainer ? View : ScreenHeaderContainer;

  return (
    <Wrapper style={styles.container}>
      {/* Left: Menu Button */}
      <View style={styles.leftButtons}>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={onMenuPress}
          activeOpacity={0.7}
        >
          <Ionicons
            name={showCalendarSelector ? 'close' : 'menu'}
            size={24}
            color={palette.tint}
          />
        </TouchableOpacity>
      </View>

      {/* Center: Day Navigation */}
      <View style={styles.navigationGroup}>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={onPrevDay}
          activeOpacity={0.7}
        >
          <Ionicons name="chevron-back" size={24} color={palette.tint} />
        </TouchableOpacity>

        {onHeaderTextPress ? (
          <TouchableOpacity
            onPress={onHeaderTextPress}
            activeOpacity={0.5}
            style={[
              styles.dateTextContainer,
              { borderColor: palette.border, backgroundColor: palette.cardBackground },
              Shadows.mdBottomRight,
            ]}
          >
            <Text
              style={[styles.dateText, { color: palette.text, fontFamily: getHeadingFont() }]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.8}
            >
              {headerText}
            </Text>
          </TouchableOpacity>
        ) : (
          <View
            style={[
              styles.dateTextContainer,
              { borderColor: palette.borderMuted, backgroundColor: palette.cardBackground },
              Shadows.smBottomRight,
            ]}
          >
            <Text
              style={[styles.dateText, { color: palette.text, fontFamily: getHeadingFont() }]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.8}
            >
              {headerText}
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.iconButton}
          onPress={onNextDay}
          activeOpacity={0.7}
        >
          <Ionicons name="chevron-forward" size={24} color={palette.tint} />
        </TouchableOpacity>
      </View>

      {/* Right: Add and Today Buttons */}
      <View style={styles.rightButtons}>
        {onAddEventPress && (
          <AddEventDropdown
            onAddEvent={onAddEventPress}
            tintColor={palette.tint}
          />
        )}
        <TouchableOpacity
          style={[styles.todayButton, { borderColor: palette.tint }]}
          onPress={onTodayPress}
          activeOpacity={0.7}
        >
          <Text style={[styles.todayButtonText, { color: palette.tint }]}>
            {new Date().getDate()}
          </Text>
        </TouchableOpacity>
      </View>
    </Wrapper>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 14,
  },
  leftButtons: {
    width: 44,
    alignItems: 'flex-start',
  },
  iconButton: {
    padding: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navigationGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: 4,
  },
  dateTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderRadius: BorderRadius.sm,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  dateText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    flexShrink: 1,
  },

  rightButtons: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  todayButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  todayButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
});
